import {
  ResponseRecord,
  SessionStatusBot,
} from '@hyper-inferno/survey-elements-core';
import {
  ISurveySession,
  SurveySessionStatus,
  SyncStatus,
} from './survey-session.interface';

export type NavigationMethod =
  | 'forward'
  | 'back'
  | 'skip_logic'
  | 'autocode'
  | 'start'
  | 'end'
  | 'jump'
  | 'plugin_jump';

export interface ISurveyActivityLog {
  questionId: string | number; // The page/question they just arrived at
  fromQuestionId?: string | number; // The page they just left
  navigationMethod: NavigationMethod; // 'forward_arrow' | 'back_arrow' | etc.
  timestamp: string; // ISO string of the event
  durationSeconds?: number; // Time spent on the *previous* page before this transition
  isAnswerChanged?: boolean; // Did they overwrite data here?
  isResetBySystem?: boolean; // Was data cleared due to skip-logic changes?
}

export interface NavigationState {
  history: number[]; // e.g., [315, 309, 304, 305, 311]
  pointer: number; // The current index the user is viewing
}

export class SurveySession implements ISurveySession {
  id!: string;
  survey_id!: number;
  session_status!: SurveySessionStatus;
  current_page_id!: number | undefined;
  survey_language!: string;
  survey_version!: number;
  session_unique_identifier!: string;

  jsn_responses!: Record<string, any>;
  jsn_session_state!: NavigationState;
  jsn_activity_order!: any;
  jsn_constructed_lists!: Record<string, any>;
  jsn_oe_mapping!: Record<string, string>;
  jsn_passive_media!: any[];

  sys_survey_start_time!: string;
  sys_survey_end_time?: string;
  sys_last_updated!: string;
  sys_profile_identifier!: string | null;
  sys_disposition_code!: number | null;
  sys_survey_delivery_status!: string | null;
  sys_jsn_device_data!: Record<string, any>;

  updated_at!: string;
  sync_status!: SyncStatus;
  last_sync_time?: string;

  constructor(data: Partial<ISurveySession>) {
    // 1. Map core fields with defaults
    this.id = data.id || crypto.randomUUID();
    this.survey_id = data.survey_id || 0;
    this.session_status =
      data.session_status || SurveySessionStatus.IN_PROGRESS;
    this.current_page_id = data.current_page_id;
    this.survey_language = data.survey_language || 'en';
    this.survey_version = data.survey_version || 1;
    this.session_unique_identifier = data.session_unique_identifier || '';
    // 2. Map JSONB objects
    this.jsn_responses = data.jsn_responses || {};
    this.jsn_session_state = data.jsn_session_state || {
      history: [],
      pointer: -1,
    };
    this.jsn_activity_order = data.jsn_activity_order || [];
    this.jsn_constructed_lists = data.jsn_constructed_lists || {};
    this.jsn_oe_mapping = data.jsn_oe_mapping || {};
    this.jsn_passive_media = data.jsn_passive_media || [];

    // 3. Map System fields
    this.sys_survey_start_time =
      data.sys_survey_start_time || new Date().toISOString();
    this.sys_survey_end_time = data.sys_survey_end_time;
    this.sys_last_updated = data.sys_last_updated || new Date().toISOString();
    this.sys_profile_identifier = data.sys_profile_identifier || null;
    this.sys_disposition_code = data.sys_disposition_code || null;
    this.sys_survey_delivery_status = data.sys_survey_delivery_status || null;
    this.sys_jsn_device_data = data.sys_jsn_device_data || {};

    // 4. Sync metadata
    this.updated_at = data.updated_at || new Date().toISOString();
    this.sync_status = data.sync_status || SyncStatus.DIRTY;
    this.last_sync_time = data.last_sync_time;
  }

  // --- Logic / Business Rules ---

  /**
   * Internal helper to mark the record as modified.
   * This triggers the need for a sync push.
   */
  private markAsDirty() {
    this.updated_at = new Date().toISOString();
    this.sync_status = SyncStatus.DIRTY;
    this.sys_last_updated = new Date().toISOString();
  }

  // answerQuestion(questionId: string, answer: any) {
  //   this.jsn_responses = { ...this.jsn_responses, [questionId]: answer };
  //   this.markAsDirty();
  // }

  logActivity(
    pageId: number | string,
    method: NavigationMethod,
    flags?: { isAnswerChanged?: boolean; isResetBySystem?: boolean },
  ) {
    const logEntry: ISurveyActivityLog = {
      questionId: pageId,
      navigationMethod: method,
      timestamp: new Date().toISOString(),
      ...flags,
    };

    // 1. Immutably append to the raw audit trail log
    this.jsn_activity_order = [...this.jsn_activity_order, logEntry];
    // const logEntryString = JSON.stringify(logEntry);
    // this.jsn_activity_order = [...this.jsn_activity_order, logEntryString];

    // 2. Manage the separate navigation state history & pointer
    const currentState = this.jsn_session_state || { history: [], pointer: -1 };

    let updatedHistory = [...(currentState['history'] ?? [])];
    let updatedPointer = currentState['pointer'] ?? -1;

    if (method === 'back') {
      // Just shift the pointer left (Browser-style Back)
      if (updatedPointer > 0) {
        updatedPointer--;
      }
    } else {
      // For 'forward', 'jump', 'skip_logic', 'start', etc.

      const nextHistoryIndex = updatedPointer + 1;

      // Check if we are just traversing forward into history that ALREADY exists
      if (
        method === 'forward' &&
        nextHistoryIndex < updatedHistory.length &&
        updatedHistory[nextHistoryIndex] === pageId
      ) {
        // Exact match found in history! Just move the pointer right.
        updatedPointer++;
      } else {
        // This is a NEW page forward, a 'jump', or a brand new path:

        // 1. Chop off any stale "forward history" ahead of our current pointer
        updatedHistory = updatedHistory.slice(0, updatedPointer + 1);
        // TODO Also clear all the answers of these deleted log entries

        // 2. Append the new page ID to our path
        updatedHistory.push(Number(pageId));

        // 3. Point to the end of our new active history
        updatedPointer = updatedHistory.length - 1;
      }
    }

    // 3. Save the clean pointer state object back
    this.jsn_session_state = {
      history: updatedHistory,
      pointer: updatedPointer,
    };

    this.markAsDirty();
  }

  /**
   * Updated to accept how the navigation happened
   */
  moveToQuestion(
    pageId: number,
    method: NavigationMethod = 'forward',
    flags?: { isAnswerChanged?: boolean; isResetBySystem?: boolean },
  ) {
    if (this.current_page_id === pageId) return;

    this.current_page_id = pageId;

    // Automatically log this movement event
    this.logActivity(pageId, method, flags);
  }

  endSurvey(status: SessionStatusBot, disposition_code = 'SURVEY_END') {
    switch (status) {
      case SessionStatusBot.COMPLETED:
        this.complete();
        break;

      case SessionStatusBot.TERMINATED:
        this.terminate(disposition_code);
        break;

      default:
        break;
    }
  }

  complete() {
    this.session_status = SurveySessionStatus.COMPLETED;
    this.sys_survey_end_time = new Date().toISOString();
    this.markAsDirty();
  }

  terminate(disposition_code: string) {
    this.session_status = SurveySessionStatus.TERMINATED;
    this.sys_survey_end_time = new Date().toISOString();
    // this.sys_disposition_code = disposition_code;
    this.markAsDirty();
  }

  markSynced() {
    this.sync_status = SyncStatus.SYNCED;
    this.last_sync_time = new Date().toISOString();
  }

  setLanguage(lang: string) {
    if (this.session_status !== SurveySessionStatus.IN_PROGRESS) return;
    if (this.survey_language === lang) return;
    this.survey_language = lang;
    this.markAsDirty();
  }

  patchOEMapping(dataToPatch: Record<string, string>) {
    this.jsn_oe_mapping = { ...this.jsn_oe_mapping, ...dataToPatch };
    this.markAsDirty();
  }

  /**
   * Merges answers into the session, keyed by element id (one answer object per
   * element, as stored in `jsn_responses`).
   */
  patchResponses(data: Record<string, ResponseRecord>) {
    this.jsn_responses = { ...this.jsn_responses, ...data };
  }

  /**
   * Returns a plain object matching the Supabase table schema exactly.
   */
  toPersistence() {
    return {
      id: this.id,
      survey_id: this.survey_id,
      session_status: this.session_status,
      current_page_id: this.current_page_id,
      survey_language: this.survey_language,
      survey_version: this.survey_version,
      jsn_responses: this.jsn_responses,
      jsn_session_state: this.jsn_session_state,
      jsn_activity_order: this.jsn_activity_order,
      jsn_constructed_lists: this.jsn_constructed_lists,
      jsn_oe_mapping: this.jsn_oe_mapping,
      jsn_passive_media: this.jsn_passive_media,
      sys_survey_start_time: this.sys_survey_start_time,
      sys_survey_end_time: this.sys_survey_end_time,
      sys_last_updated: this.sys_last_updated,
      sys_profile_identifier: this.sys_profile_identifier,
      sys_disposition_code: this.sys_disposition_code,
      sys_survey_delivery_status: this.sys_survey_delivery_status,
      sys_jsn_device_data: this.sys_jsn_device_data,
    };
  }
}
